"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";

import { RollWidget, RollWidgetFrame } from "@/components/roll-widgets";
import { useReplay, type ReplayTake } from "@/contexts/replay-context";
import { useShareLink } from "@/hooks/use-share-link";
import {
  RECITALS_RECORDINGS_PATH,
  recitalPath,
  takePath,
} from "@/lib/live/recital-routes";
import { recordingLength } from "@/lib/live/recording-length";

/**
 * Les prises de son propre enregistrement, posées sur le rouleau.
 *
 * # Pourquoi ce widget existe (2026-09-22)
 *
 * Publier se faisait dans la salle d'attente, c'est-à-dire **avant** d'avoir
 * regardé : on ouvrait une page qui dit « terminé », on cliquait « Publier le
 * replay », et on découvrait après coup ce qu'on venait de montrer. Or on
 * publie ce qu'on a écouté. Le geste est donc là où l'on écoute — dans le
 * studio, à côté du rouleau qui joue la prise.
 *
 * Et il vient avec les deux autres qui manquaient : **supprimer** l'essai
 * raté, qui occupait le seau pour toujours, et **recopier le lien** de ce
 * qu'on vient de publier, qui est la raison d'avoir publié. Puis un quatrième
 * (2026-09-25) : **nommer** la prise, puisque c'est sous ce nom qu'elle se
 * partage — le nom tiré au sort à « Enregistrer » ne dit rien d'elle.
 *
 * # Trois gestes, un sujet
 *
 * Le sujet est la prise **ouverte** : celle qu'on entend. Cliquer une ligne
 * l'ouvre au même endroit du morceau (`goToTake`) — c'est ce qui permet de
 * comparer deux essais —, et le pied du widget agit sur elle. Un jeu de
 * boutons par ligne aurait tenu dans la largeur, mais aurait aussi permis de
 * publier une prise qu'on n'écoute pas, ce qui est exactement le défaut qu'on
 * corrige.
 *
 * Un faux départ n'a pas de morceau à ouvrir ; il se **désigne** quand même —
 * la ligne se choisit sans que la tête de lecture bouge —, parce que c'est le
 * premier qu'on veut supprimer.
 *
 * # Seulement chez soi
 *
 * `mine` : un visiteur ne publie ni ne supprime rien, et lister les essais à
 * qui n'a pas joué reviendrait à les lui annoncer. Il garde le sélecteur de la
 * barre d'outils, qui ne montre que ce qui est publié.
 */
export function ReplayTakesWidget() {
  const replay = useReplay();
  /*
    Replié, il ne reste qu'une pastille.

    Un widget de 340 px prend le tiers d'un téléphone couché, et on est venu
    regarder le rouleau. La croix de l'en-tête le range donc au lieu de le faire
    disparaître : la pastille garde le compte des prises et le rouvre d'un clic.
    Un widget qu'on ferme pour de bon n'aurait plus de porte — c'est le seul
    endroit d'où l'on publie et supprime.
  */
  const [open, setOpen] = useState(true);

  if (!replay.mine || replay.status !== "ready") {
    return null;
  }

  return (
    <RollWidget anchor="top-left">
      {open ? (
        <TakesPanel onClose={() => setOpen(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-8 items-center gap-2 rounded-lg border border-border-subtle px-3 font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary transition-colors duration-150 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{
            background: "var(--color-glass-shell)",
            backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
            WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
          }}
        >
          Prises
          <span className="tabular-nums text-text-muted">
            {replay.takes.length > 0 ? replay.takes.length : "—"}
          </span>
        </button>
      )}
    </RollWidget>
  );
}

/** La largeur : un lien de prise tient en deux lignes à 340 px, en trois à 300. */
const WIDTH_PX = 340;

/** Au-delà, la liste défile plutôt que de descendre jusqu'au clavier. */
const LIST_MAX_PX = 208;

/** Ce que le pied du widget est en train de faire. Une seule chose à la fois. */
type Mode = "idle" | "confirming" | "renaming" | "working";

function TakesPanel({ onClose }: { onClose: () => void }) {
  const replay = useReplay();
  const router = useRouter();
  const { copied, share } = useShareLink();

  /*
    La prise désignée, quand ce n'est pas celle qu'on écoute.

    Elle ne sert qu'aux faux départs : eux seuls se désignent sans s'ouvrir.
    Elle retient **contre quelle prise ouverte** elle a été posée, et se périme
    dès que la tête de lecture en change — en cliquant une ligne jouable, ou en
    jouant jusqu'au bout de la précédente. Ce qui se lit au rendu plutôt que de
    se remettre à zéro dans un effet : une désignation qui suit la lecture est
    une valeur dérivée, pas un état à synchroniser.
  */
  const [picked, setPicked] = useState<{ take: number; whenOpen: number } | null>(null);
  const [mode, setMode] = useState<Mode>("idle");
  const [failure, setFailure] = useState<string | null>(null);
  /** Ce qu'une suppression vient de rendre, à dire une fois. */
  const [freed, setFreed] = useState<number | null>(null);
  /** Le nom part à l'API : le champ reste affiché, ses boutons attendent. */
  const [saving, setSaving] = useState(false);

  const current = replay.current;
  const chosen = picked !== null && picked.whenOpen === current ? picked.take : current;
  const selected = replay.takes.find((take) => take.number === chosen) ?? null;

  const choose = useCallback(
    (take: ReplayTake) => {
      setMode("idle");
      setFailure(null);
      // Le mot de la suppression parlait de la prise d'avant : le garder sous
      // une autre ligne le ferait lire comme s'il la concernait.
      setFreed(null);

      if (take.playable) {
        replay.goToTake(take.number);

        return;
      }

      setPicked({ take: take.number, whenOpen: replay.current });
    },
    [replay],
  );

  const publish = useCallback(
    async (published: boolean) => {
      if (selected === null) {
        return;
      }

      setMode("working");
      setFailure(null);
      setFreed(null);

      const done = await replay.publishTake(selected.number, published);

      setMode("idle");

      if (!done) {
        setFailure(
          published
            ? "La prise n'a pas pu être publiée."
            : "La prise n'a pas pu être retirée.",
        );
      }
    },
    [replay, selected],
  );

  const rename = useCallback(
    async (title: string) => {
      if (selected === null) {
        return;
      }

      setSaving(true);
      setFailure(null);
      setFreed(null);

      const done = await replay.renameTake(selected.number, title);

      setSaving(false);

      if (!done) {
        setFailure("Le nom n'a pas pu être enregistré.");

        return;
      }

      setMode("idle");
    },
    [replay, selected],
  );

  const remove = useCallback(async () => {
    if (selected === null) {
      return;
    }

    setMode("working");
    setFailure(null);

    const left = await replay.deleteTake(selected.number);

    if (left === null) {
      setMode("idle");
      setFailure("La prise n'a pas pu être supprimée. Réessaie dans un moment.");

      return;
    }

    /*
      C'était la dernière : le dépôt est parti avec elle, et il n'y a plus rien
      à regarder ici. On s'en va vers la liste des enregistrements plutôt que de
      laisser un studio qui rejoue le vide.
    */
    if (left.recording === null) {
      router.push(RECITALS_RECORDINGS_PATH);

      return;
    }

    setMode("idle");
    setFreed(left.freedBytes);
  }, [replay, router, selected]);

  const link = (take: ReplayTake): string =>
    `${window.location.origin}${takePath(take.id)}`;

  return (
    <RollWidgetFrame
      kicker="Prises"
      width={WIDTH_PX}
      ariaLabel="Les prises de cet enregistrement"
      onClose={onClose}
      trailing={
        <span className="font-mono text-[10px] tabular-nums text-text-muted">
          {replay.takes.length > 0 ? replay.takes.length : "—"}
        </span>
      }
      footer={
        selected === null ? undefined : mode === "confirming" ? (
          <Confirm
            take={selected}
            last={replay.takes.length <= 1}
            onCancel={() => setMode("idle")}
            onConfirm={() => void remove()}
          />
        ) : mode === "renaming" ? (
          <Rename
            // Une autre prise, un autre brouillon : le champ repart de son nom.
            key={selected.number}
            take={selected}
            fallback={replay.title ?? ""}
            busy={saving}
            onCancel={() => {
              setMode("idle");
              setFailure(null);
            }}
            onSave={(title) => void rename(title)}
          />
        ) : (
          <Actions
            take={selected}
            busy={mode === "working"}
            copied={copied}
            fallback={replay.title ?? ""}
            onPublish={(published) => void publish(published)}
            onShare={() =>
              void share(link(selected), {
                title: selected.title ?? replay.title ?? "Virtuosa",
              })
            }
            onRename={() => {
              setMode("renaming");
              setFailure(null);
              setFreed(null);
            }}
            onDelete={() => setMode("confirming")}
          />
        )
      }
    >
      <div className="px-2 py-2" style={{ maxHeight: LIST_MAX_PX, overflowY: "auto" }}>
        {replay.takes.length === 0 ? (
          <SingleRun />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {replay.takes.map((take) => (
              <Row
                key={take.number}
                take={take}
                open={take.number === chosen}
                onChoose={() => choose(take)}
              />
            ))}
          </ul>
        )}
      </div>

      {failure !== null && (
        <p role="alert" className="m-0 px-4 pb-2 text-[11px] leading-snug text-err">
          {failure}
        </p>
      )}

      {freed !== null && failure === null && (
        <p className="m-0 px-4 pb-2 text-[11px] leading-snug text-text-muted">
          Prise supprimée{freed > 0 ? ` · ${weight(freed)} rendus` : ""}.
        </p>
      )}
    </RollWidgetFrame>
  );
}

/**
 * Une ligne : son nom propre, sinon son rang ; ce qu'il y a dedans, et sa
 * pastille si elle est en ligne. Nommée, elle garde son rang en info-bulle :
 * c'est lui qui la situe parmi les autres essais.
 *
 * Un bouton et non un lien : il ne s'agit pas d'aller ailleurs mais de poser la
 * tête de lecture dans un autre morceau de la même séquence, au même endroit.
 */
function Row({
  take,
  open,
  onChoose,
}: {
  take: ReplayTake;
  open: boolean;
  onChoose: () => void;
}) {
  return (
    <li className="m-0">
      <button
        type="button"
        onClick={onChoose}
        aria-current={open ? "true" : undefined}
        className={`flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-left transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          open ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"
        }`}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden="true"
            className={`size-1.5 shrink-0 rounded-full ${
              take.published ? "bg-accent-bright" : "bg-transparent"
            }`}
          />
          <span
            title={take.title === null ? undefined : take.label}
            className={`truncate text-[13px] ${
              open ? "font-medium text-text-primary" : "text-text-secondary"
            }`}
          >
            {take.title ?? take.label}
          </span>
          {take.published && (
            <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.12em] text-accent-bright">
              en ligne
            </span>
          )}
        </span>

        <span className="shrink-0 font-mono text-[10px] tabular-nums text-text-muted">
          {/*
            Un essai à zéro note est un faux départ : le dire vaut mieux que
            d'afficher « 0 note », qui se lit comme un compteur en panne. Sa
            durée reste, c'est elle qui dit ce qu'il occupe.
          */}
          {take.playable ? `${recordingLength(take.durationMs)} · ${take.notes} notes` : "faux départ"}
        </span>
      </button>
    </li>
  );
}

/**
 * Le pied du widget : ce qu'on fait de la prise qu'on écoute.
 *
 * En tête, le nom sous lequel elle se partage — le sien, sinon celui de
 * l'enregistrement —, et de quoi le changer : sans cette ligne, l'interprète
 * ne voyait nulle part le nom que son lien allait montrer.
 *
 * Publiée, c'est le **lien** qui prend la place du bouton : c'est lui qu'on est
 * venu chercher, et il n'y a rien d'autre à faire une fois publié. « Retirer »
 * reste à côté, en petit — savoir qu'on peut défaire est ce qui permet de
 * faire.
 */
function Actions({
  take,
  busy,
  copied,
  fallback,
  onPublish,
  onShare,
  onRename,
  onDelete,
}: {
  take: ReplayTake;
  busy: boolean;
  copied: boolean;
  /** Le nom de l'enregistrement, que prend une prise sans nom propre. */
  fallback: string;
  onPublish: (published: boolean) => void;
  onShare: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[12px] text-text-secondary">
          <span className="text-text-muted">Nom · </span>
          {take.title ?? fallback}
        </span>
        <GhostButton busy={busy} onClick={onRename}>
          Renommer
        </GhostButton>
      </div>

      <div className="flex w-full flex-wrap items-center gap-2">
        {take.published ? (
          <>
            <button
              type="button"
              onClick={onShare}
              className="h-7 shrink-0 rounded-md bg-accent px-2.5 text-[12px] font-semibold text-text-primary transition-colors duration-150 hover:bg-accent-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {copied ? "Lien copié" : "Partager le lien"}
            </button>
            <GhostButton busy={busy} onClick={() => onPublish(false)}>
              Retirer
            </GhostButton>
          </>
        ) : (
          <button
            type="button"
            disabled={busy || !take.playable}
            onClick={() => onPublish(true)}
            title={take.playable ? undefined : "Un faux départ n'a rien à montrer."}
            className="h-7 shrink-0 rounded-md bg-accent px-2.5 text-[12px] font-semibold text-text-primary transition-colors duration-150 hover:bg-accent-bright disabled:cursor-default disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {busy ? "· · ·" : "Publier cette prise"}
          </button>
        )}

        <GhostButton busy={busy} danger onClick={onDelete}>
          Supprimer
        </GhostButton>
      </div>
    </div>
  );
}

/**
 * Nommer la prise, à la place des boutons — comme la confirmation : la liste
 * reste visible au-dessus, et c'est bien celle-là qu'on nomme.
 *
 * Le champ porte le nom **propre** de la prise, vide quand elle n'en a pas ;
 * le nom d'emprunt — celui de l'enregistrement — s'y lit en grisé. Le même
 * geste que dans « Mes enregistrements » (`my-recordings.tsx`) : vider et
 * valider rend le nom d'emprunt.
 */
function Rename({
  take,
  fallback,
  busy,
  onCancel,
  onSave,
}: {
  take: ReplayTake;
  fallback: string;
  busy: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
}) {
  const [draft, setDraft] = useState(take.title ?? "");

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
      className="flex w-full flex-col gap-2"
    >
      <input
        // Le champ remplace les boutons sur un geste explicite : le focus est
        // ce qu'on vient de demander.
        autoFocus
        value={draft}
        disabled={busy}
        maxLength={80}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
        placeholder={fallback}
        aria-label={`Nom de la prise, tel qu'il sera partagé, actuellement ${take.title ?? fallback}`}
        className="h-8 w-full rounded-lg border border-border-strong bg-surface-card-hover px-2.5 text-[13px] text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      />
      <p className="m-0 text-[11px] leading-[1.45] text-text-muted">
        C&apos;est le nom sous lequel elle se partage. Vide, elle prend celui de
        l&apos;enregistrement.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="h-7 shrink-0 rounded-md bg-accent px-2.5 text-[12px] font-semibold text-text-primary transition-colors duration-150 hover:bg-accent-bright disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {busy ? "· · ·" : "Enregistrer"}
        </button>
        <GhostButton busy={busy} onClick={onCancel}>
          Annuler
        </GhostButton>
      </div>
    </form>
  );
}

/**
 * La confirmation, à la place des boutons — pas une fenêtre modale : elle
 * couvrirait la liste au moment précis où l'on vérifie que c'est bien
 * celle-là. Elle nomme ce qui part, au présent, et se quitte par Échap.
 */
function Confirm({
  take,
  last,
  onCancel,
  onConfirm,
}: {
  take: ReplayTake;
  last: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="flex w-full flex-col gap-2"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onCancel();
        }
      }}
    >
      <p className="m-0 text-[11px] leading-[1.45] text-text-secondary">
        {take.title ?? take.label} part pour de bon : ses notes, ses images et son MIDI.
        {last && " C'est la dernière — l'enregistrement s'en va avec elle."}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          // La confirmation vient de remplacer le bouton qui avait le focus ;
          // sans ça il retomberait sur le `body`, et Échap n'irait nulle part.
          autoFocus
          onClick={onConfirm}
          className="h-7 shrink-0 rounded-md bg-err px-2.5 text-[12px] font-semibold text-white transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Supprimer pour de bon
        </button>
        <GhostButton busy={false} onClick={onCancel}>
          Annuler
        </GhostButton>
      </div>
    </div>
  );
}

/**
 * Un enregistrement d'une seule traversée : il n'a pas de prises, et il n'y a
 * donc rien à choisir. C'est le tout qui se publie, au lien du récital.
 */
function SingleRun() {
  const replay = useReplay();
  const [busy, setBusy] = useState(false);
  const { copied, share } = useShareLink();

  const toggle = async (next: boolean) => {
    setBusy(true);
    await replay.publishAll(next);
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-2.5 px-2.5 py-1.5">
      <p className="m-0 text-[12px] leading-[1.45] text-text-secondary">
        Une seule traversée : il n&apos;y a pas d&apos;essai à choisir, c&apos;est
        l&apos;enregistrement entier qui se publie.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {replay.published ? (
          <>
            <button
              type="button"
              onClick={() =>
                void share(`${window.location.origin}${recitalPath(replay.recitalId ?? "")}`, {
                  title: replay.title ?? "Virtuosa",
                })
              }
              className="h-7 shrink-0 rounded-md bg-accent px-2.5 text-[12px] font-semibold text-text-primary transition-colors duration-150 hover:bg-accent-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {copied ? "Lien copié" : "Partager le lien"}
            </button>
            <GhostButton busy={busy} onClick={() => void toggle(false)}>
              Retirer
            </GhostButton>
          </>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggle(true)}
            className="h-7 shrink-0 rounded-md bg-accent px-2.5 text-[12px] font-semibold text-text-primary transition-colors duration-150 hover:bg-accent-bright disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {busy ? "· · ·" : "Publier l'enregistrement"}
          </button>
        )}
      </div>
    </div>
  );
}

function GhostButton({
  busy,
  danger = false,
  onClick,
  children,
}: {
  busy: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={`h-7 shrink-0 rounded-md px-2 text-[12px] font-medium transition-colors duration-150 cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        danger
          ? "text-err hover:bg-err/10"
          : "text-text-secondary hover:bg-white/[0.06] hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

/** « 148 Mo », « 1,2 Go » : la place rendue, dans l'unité qui se lit. */
function weight(bytes: number): string {
  if (bytes >= 1_000_000_000) {
    return `${(bytes / 1_000_000_000).toFixed(1).replace(".", ",")} Go`;
  }

  if (bytes >= 1_000_000) {
    return `${Math.round(bytes / 1_000_000)} Mo`;
  }

  return `${Math.max(1, Math.round(bytes / 1_000))} Ko`;
}
